import React, { useEffect, useState } from "react";
import { isViewEnabled } from "../features";

function MenuIcon({ open }) {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden>
      {open ? (
        <path d="M18.3 5.71 12 12l6.3 6.29-1.41 1.42L10.17 12l6.72-7.71 1.41 1.42z" fill="currentColor" />
      ) : (
        <path d="M3 6h18v2H3V6zm0 5h18v2H3v-2zm0 5h18v2H3v-2z" fill="currentColor" />
      )}
    </svg>
  );
}

const PURCHASE_VIEWS = [
  { id: "inward", label: "Material Purchase" },
  { id: "purchaseReports", label: "Material Purchase Report" },
];

const SALES_VIEWS = [
  { id: "billing", label: "Sales" },
  { id: "billingReport", label: "Sales Report" },
  { id: "billingSummary", label: "Sales Summary Report" },
];

const MASTER_VIEWS = [
  { id: "storeMaster", label: "Store Master" },
  { id: "itemMaster", label: "Item Master" },
  { id: "supplierMaster", label: "Supplier Master" },
  { id: "customerMaster", label: "Customer Master" },
  { id: "expenseNameMaster", label: "Expense Master" },
  { id: "employeeMaster", label: "Employee Master" },
  { id: "employeeAttendance", label: "Employee Attendance" },
];

const LEGACY_EXPENSE_VIEW = "miscExpenses";

const VIEW_LABELS = {
  dashboard: "Dashboard",
  paymentLedger: "Payment Ledger",
  amountReceived: "Daily Amount Received",
  inward: "Material Purchase",
  purchaseReports: "Material Purchase Report",
  billing: "Sales",
  billingReport: "Sales Report",
  billingSummary: "Sales Summary Report",
  expenseDelivery: "Delivery Expenses",
  expenseStall: "Stall Expenses",
  expenseMarket: "Market Expenses",
  expenseMaster: "Master Expense",
  salesStall: "Stall Billing",
  stallReports: "Stall Reports",
  salesMarket: "Market Credit Sales",
  marketReports: "Market Reports",
  inwardStall: "Stall - Inward & Sales",
  inwardMarket: "Market - Material Inward",
  itemMaster: "Item Master",
  supplierMaster: "Supplier Master",
  customerMaster: "Customer Master",
  storeMaster: "Store Master",
  expenseNameMaster: "Expense Master",
  employeeMaster: "Employee Master",
  employeeAttendance: "Employee Attendance",
  userAccess: "User Rights",
  billRequests: "Admin Approval",
  miscExpenses: "Delivery Expenses",
  noAccess: "No Access",
};

function viewTitle(view) {
  return VIEW_LABELS[view] || "";
}

function formatLiveDateTime(value) {
  const date = new Date(value);
  const dateText = new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: "Asia/Kolkata",
  }).format(date);
  const timeText = new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    timeZone: "Asia/Kolkata",
  }).format(date);
  return { dateText, timeText };
}

export default function Layout({ currentView, onNavigate, currentUser, onLogout, children }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [purchaseOpen, setPurchaseOpen] = useState(true);
  const [salesOpen, setSalesOpen] = useState(true);
  const [mastersOpen, setMastersOpen] = useState(true);
  const [now, setNow] = useState(() => Date.now());
  const isAdmin = (currentUser?.role || "") === "admin";
  const allowedViews = Array.isArray(currentUser?.allowedViews) ? currentUser.allowedViews : [];

  const canAccess = (view) =>
    isViewEnabled(view) &&
    (view !== "storeMaster" || isAdmin) &&
    (isAdmin ||
      allowedViews.includes(view === "billingSummary" ? "billingReport" : view) ||
      (view === "expenseMaster" && allowedViews.includes(LEGACY_EXPENSE_VIEW)));

  const visiblePurchase = PURCHASE_VIEWS.filter((item) => canAccess(item.id));
  const visibleSales = SALES_VIEWS.filter((item) => canAccess(item.id));
  const visibleMasters = MASTER_VIEWS.filter((item) => canAccess(item.id));

  const purchaseActive = visiblePurchase.some((item) => item.id === currentView);
  const salesActive = visibleSales.some((item) => item.id === currentView);
  const mastersActive = visibleMasters.some((item) => item.id === currentView);

  const go = (view) => {
    onNavigate(view);
    setDrawerOpen(false);
  };
  const brandLogoSrc = `${process.env.PUBLIC_URL || ""}/Logo.png`;
  const { dateText, timeText } = formatLiveDateTime(now);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const renderGroup = (title, items, open, setOpen, active, tone = "") => {
    if (!items.length) return null;
    return (
      <div className={`nav-group nav-group-card ${tone ? `nav-group-card-${tone}` : ""}`}>
        <button
          type="button"
          className={`nav-item nav-group-trigger ${active ? "active" : ""}`}
          onClick={() => setOpen((prev) => !prev)}
          aria-expanded={open}
        >
          <span className="nav-group-text">
            {tone ? <span className={`nav-group-icon nav-group-icon-${tone}`} aria-hidden /> : null}
            <span className="nav-group-kicker">Section</span>
            <span className="nav-group-title">{title}</span>
          </span>
          <span className={`caret ${open ? "open" : ""}`} aria-hidden>
            &gt;
          </span>
        </button>
        {open ? (
          <div className="nav-sub">
            {items.map((item) => {
              const isActive = currentView === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  className={`nav-item sub ${isActive ? "active" : ""}`}
                  onClick={() => go(item.id)}
                  aria-current={isActive ? "page" : undefined}
                >
                  {item.label}
                </button>
              );
            })}
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div className={`layout ${drawerOpen ? "drawer-open" : ""}`}>
      <header className="topbar">
        <button className="icon-btn" onClick={() => setDrawerOpen((v) => !v)} aria-label="Toggle menu">
          <MenuIcon open={drawerOpen} />
        </button>
        <div className="brand-identity">
          <div className="brand-logo-wrap">
            <img className="brand-logo" src={brandLogoSrc} alt="ARF Seafoods logo" />
          </div>
          <div className="brand-lockup">
            <div className="brand">ARF Seafoods</div>
            <div className="brand-subtitle">Operations Console</div>
          </div>
        </div>
        <span className="divider" aria-hidden>
          |
        </span>
        <div className="top-title" role="heading" aria-level={1}>
          {viewTitle(currentView)}
        </div>
        <div className="topbar-right">
          <div className="date-time-pill" aria-label={`Current date ${dateText} and time ${timeText}`}>
            <span className="date-time-date">{dateText}</span>
            <span className="date-time-sep">|</span>
            <span className="date-time-time">{timeText}</span>
          </div>
          {currentUser ? <div className="user-pill">{currentUser.username}</div> : null}
          {onLogout ? (
            <button type="button" className="topbar-btn" onClick={onLogout}>
              Logout
            </button>
          ) : null}
        </div>
      </header>

      <aside className="drawer">
        <div className="drawer-header">
          <div className="drawer-kicker">Navigation</div>
          <div className="drawer-title-card">Choose a work area</div>
          <div className="drawer-copy">Purchase and sales operations in one place.</div>
        </div>
        <nav className="nav">
          {canAccess("dashboard") ? (
            <button
              type="button"
              className={`nav-item main nav-item-standalone ${currentView === "dashboard" ? "active" : ""}`}
              onClick={() => go("dashboard")}
              aria-current={currentView === "dashboard" ? "page" : undefined}
            >
              Dashboard
            </button>
          ) : null}

          {canAccess("paymentLedger") ? (
            <button
              type="button"
              className={`nav-item main nav-item-standalone ${currentView === "paymentLedger" ? "active" : ""}`}
              onClick={() => go("paymentLedger")}
              aria-current={currentView === "paymentLedger" ? "page" : undefined}
            >
              Payment Ledger
            </button>
          ) : null}

          {renderGroup("Purchase", visiblePurchase, purchaseOpen, setPurchaseOpen, purchaseActive, "purchase")}
          {renderGroup("Sales", visibleSales, salesOpen, setSalesOpen, salesActive, "sales")}
          {renderGroup("Masters", visibleMasters, mastersOpen, setMastersOpen, mastersActive, "masters")}

          {canAccess("expenseMaster") ? (
            <button
              type="button"
              className={`nav-item main nav-item-standalone ${currentView === "expenseMaster" ? "active" : ""}`}
              onClick={() => go("expenseMaster")}
              aria-current={currentView === "expenseMaster" ? "page" : undefined}
            >
              Master Expense
            </button>
          ) : null}

          {isAdmin ? (
            <button
              type="button"
              className={`nav-item main nav-item-standalone ${currentView === "userAccess" ? "active" : ""}`}
              onClick={() => go("userAccess")}
              aria-current={currentView === "userAccess" ? "page" : undefined}
            >
              User Rights
            </button>
          ) : null}

          {isAdmin ? (
            <button
              type="button"
              className={`nav-item main nav-item-standalone ${currentView === "billRequests" ? "active" : ""}`}
              onClick={() => go("billRequests")}
              aria-current={currentView === "billRequests" ? "page" : undefined}
            >
              Admin Approval
            </button>
          ) : null}
        </nav>
      </aside>

      <div className="overlay" onClick={() => setDrawerOpen(false)} />

      <main className="content">{children}</main>
      <footer className="footer">© {new Date().getFullYear()} ARF Seafoods</footer>
    </div>
  );
}
